// Field mapping utilities for converting between country-specific and generic field names
// This supports the multi-country payroll system by normalizing field names

import { CountryCode } from '../core/types';

/**
 * Mapping of country-specific field names to generic equivalents
 */
export const COUNTRY_FIELD_MAPPINGS: Record<CountryCode, Record<string, string>> = {
  'SK': {
    // Personal identification
    'rodne_cislo': 'personal_identification_number',
    'applies_tax_free_allowance': 'applies_personal_tax_allowance',
    'applies_spouse_allowance': 'applies_spouse_tax_allowance', 
    'applies_child_tax_bonus': 'applies_child_tax_credit',
    
    // Contract and employment
    'contract_start_date': 'employment_start_date',
    'contract_type': 'employment_contract_type',
    'third_pillar_contribution': 'voluntary_pension_contribution',
    'union_contribution': 'union_dues',
    
    // Health and benefits
    'has_severe_disability': 'has_disability_status',
    'health_insurance_company': 'health_insurance_provider'
  },
  
  'DE': {
    // Personal identification  
    'steuer_id': 'personal_identification_number',
    'tax_class': 'tax_classification',
    'church_tax': 'religious_tax_rate',
    
    // Employment
    'contract_type': 'employment_contract_type',
    'employment_start_date': 'employment_start_date',
    'probation_period_months': 'probation_period_duration'
  },
  
  'US': {
    // Personal identification
    'ssn': 'personal_identification_number',
    'federal_tax_allowances': 'federal_tax_exemptions',
    'state_tax_allowances': 'state_tax_exemptions',
    
    // Employment
    'employment_type': 'employment_contract_type',
    'hire_date': 'employment_start_date'
  },
  
  'GB': {
    // Personal identification
    'ni_number': 'personal_identification_number',
    'tax_code': 'tax_classification',
    'p45_provided': 'previous_employment_document_provided',
    
    // Employment  
    'contract_type': 'employment_contract_type',
    'start_date': 'employment_start_date'
  }
};

/**
 * Reverse mappings for converting generic names back to country-specific
 */
export const GENERIC_FIELD_MAPPINGS: Record<CountryCode, Record<string, string>> = {};

// Build reverse mappings
Object.entries(COUNTRY_FIELD_MAPPINGS).forEach(([countryCode, mapping]) => {
  GENERIC_FIELD_MAPPINGS[countryCode] = {};
  Object.entries(mapping).forEach(([countryField, genericField]) => {
    GENERIC_FIELD_MAPPINGS[countryCode][genericField] = countryField;
  });
});

/**
 * Convert country-specific field names to generic equivalents
 */
export function normalizeFieldNames<T extends Record<string, any>>(
  data: T, 
  countryCode: CountryCode
): Record<string, any> {
  const mapping = COUNTRY_FIELD_MAPPINGS[countryCode];
  if (!mapping) {
    return data;
  }
  
  const normalized: Record<string, any> = {};
  
  Object.entries(data).forEach(([fieldName, value]) => {
    const genericFieldName = mapping[fieldName] || fieldName;
    normalized[genericFieldName] = value;
  });
  
  return normalized;
}

/**
 * Convert generic field names to country-specific equivalents
 */
export function denormalizeFieldNames<T extends Record<string, any>>(
  data: T, 
  countryCode: CountryCode
): Record<string, any> {
  const mapping = GENERIC_FIELD_MAPPINGS[countryCode];
  if (!mapping) {
    return data;
  }
  
  const denormalized: Record<string, any> = {};
  
  Object.entries(data).forEach(([fieldName, value]) => {
    const countryFieldName = mapping[fieldName] || fieldName;
    denormalized[countryFieldName] = value;
  });
  
  return denormalized;
}

/**
 * Get the country-specific field name for a generic field
 */
export function getCountryFieldName(
  genericFieldName: string, 
  countryCode: CountryCode
): string {
  const mapping = GENERIC_FIELD_MAPPINGS[countryCode];
  return mapping?.[genericFieldName] || genericFieldName;
}

/**
 * Get the generic field name for a country-specific field
 */
export function getGenericFieldName(
  countryFieldName: string, 
  countryCode: CountryCode
): string {
  const mapping = COUNTRY_FIELD_MAPPINGS[countryCode];
  return mapping?.[countryFieldName] || countryFieldName;
}

/**
 * Get all available field mappings for a country
 */
export function getCountryFieldMappings(countryCode: CountryCode): {
  countryToGeneric: Record<string, string>;
  genericToCountry: Record<string, string>;
} {
  return {
    countryToGeneric: COUNTRY_FIELD_MAPPINGS[countryCode] || {},
    genericToCountry: GENERIC_FIELD_MAPPINGS[countryCode] || {}
  };
}

/**
 * Validate that all required country-specific fields have generic equivalents
 */
export function validateFieldMappings(
  requiredFields: string[], 
  countryCode: CountryCode
): {
  valid: boolean;
  missingMappings: string[];
  availableMappings: string[];
} {
  const mapping = COUNTRY_FIELD_MAPPINGS[countryCode] || {};
  const availableMappings = Object.keys(mapping);
  const missingMappings = requiredFields.filter(field => !mapping[field]);
  
  return {
    valid: missingMappings.length === 0,
    missingMappings,
    availableMappings
  };
}

/**
 * Country-specific field descriptions for UI display
 */
export const FIELD_DESCRIPTIONS: Record<CountryCode, Record<string, string>> = {
  'SK': {
    'rodne_cislo': 'Slovak personal number (rodné číslo) - format: YYMMDD/XXXX',
    'contract_type': 'Employment contract type (TPP, HALF_TIME, DoVP, DoPČ, DoBPŠ)',
    'applies_tax_free_allowance': 'Apply tax-free allowance (NČZD)',
    'applies_spouse_allowance': 'Apply spouse tax allowance',
    'applies_child_tax_bonus': 'Apply child tax bonus',
    'health_insurance_company': 'Health insurance provider (VšZP, Dôvera, Union)'
  },
  
  'DE': {
    'steuer_id': 'German tax identification number - 11 digits',
    'contract_type': 'Employment type (VOLLZEIT, TEILZEIT, MINIJOB, MIDIJOB)',
    'tax_class': 'Tax class (1-6) based on marital status',
    'church_tax': 'Church tax percentage (8% or 9%)'
  },
  
  'US': {
    'ssn': 'Social Security Number - format: XXX-XX-XXXX',
    'employment_type': 'Employment type (AT_WILL, CONTRACT)',
    'federal_tax_allowances': 'Federal tax exemptions claimed',
    'state_tax_allowances': 'State tax exemptions claimed'
  },
  
  'GB': {
    'ni_number': 'National Insurance number - format: XX123456X',
    'tax_code': 'PAYE tax code (e.g., 1257L)',
    'contract_type': 'Contract type (PERMANENT, FIXED_TERM, ZERO_HOURS)',
    'p45_provided': 'P45 form provided from previous employer'
  }
};

/**
 * Get field description for a country-specific field
 */
export function getFieldDescription(
  fieldName: string, 
  countryCode: CountryCode
): string {
  const descriptions = FIELD_DESCRIPTIONS[countryCode] || {};
  return descriptions[fieldName] || `${fieldName} (${countryCode}-specific field)`;
}

/**
 * Utility type for strongly typed field mapping
 */
export type MappedFields<T extends Record<string, any>, C extends CountryCode> = {
  [K in keyof T]: K extends keyof (typeof COUNTRY_FIELD_MAPPINGS)[C] 
    ? (typeof COUNTRY_FIELD_MAPPINGS)[C][K] 
    : K;
};

/**
 * Type-safe field normalizer function
 */
export function normalizeFieldNamesTyped<
  T extends Record<string, any>,
  C extends CountryCode
>(data: T, countryCode: C): MappedFields<T, C> {
  return normalizeFieldNames(data, countryCode) as MappedFields<T, C>;
}